import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { amplitudeContextSelector } from '@/features/amplitude/store/amplitude';
import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  IgtMessages,
  ProviderLoggerActionNames,
  ProviderLoggerMessages,
} from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { processPayload } from '@/utils/process-error-payload';

export const useIgtEventHandlers = () => {
  const [audioSettingsReceived, setAudioSettingsReceived] = useState(false);

  const { gameSwitchEnabled } = useSelector(amplitudeContextSelector);

  const handleGameLoadStarted = useCallback(() => {
    WrapperProxy.setGameLoadStarted();
  }, []);

  const handleGameLoaded = useCallback(() => {
    WrapperProxy.setGameLoaded();
  }, []);

  const handleGameLoadProgress = useCallback((payload) => {
    WrapperProxy.setGameLoadProgress(payload.loadingPercent);
  }, []);

  const handleSpinStarted = useCallback(() => {
    WrapperProxy.setSpinInProgress(true);
  }, []);

  const handleSpinEnd = useCallback(() => {
    WrapperProxy.setSpinInProgress(false);
  }, []);

  const handleGameInProgressReload = useCallback(
    (payload) => {
      Logger.rumAction({
        name: ProviderLoggerActionNames.GAME_SWITCH_BROKEN_FLOW,
        useCase: DdEventCategory.ProviderIntegration,
        description: ProviderLoggerMessages.BROKEN_FLOW,
        eventType: DdEventType.Info,
        context: {
          provider: Provider.IGT,
          rgsCode: payload.softwareid,
          stringifiedPayload: processPayload(payload),
        },
      });

      WrapperProxy.setGameSwitch(
        {
          rgsCode: payload.softwareid,
          provider: Provider.IGT,
        },
        gameSwitchEnabled ?? false,
      );
    },
    [gameSwitchEnabled],
  );

  const handleGameReset = useCallback(() => WrapperProxy.resetGame(), []);

  const handleAudio = useCallback(
    (payload) => {
      if (payload.settingName === 'sound') {
        WrapperProxy.setAudio(payload.settingValue === 'on');
        if (!audioSettingsReceived) {
          setAudioSettingsReceived(true);
        }
      }
    },
    [audioSettingsReceived],
  );

  const handleError = useCallback((payload) => {
    const errorType = getErrorType(
      payload.errorCode,
      Provider.IGT,
      false,
      payload,
    );

    WrapperProxy.setProviderError({
      display: true,
      errorCode: payload.errorCode,
      errorMessage: payload.errorMessage,
      errorParameters: payload?.errorParameters,
      errorType,
    });
  }, []);

  const providerEventHandlerMapper = useMemo(
    () => ({
      [IgtMessages.GAME_LOAD_COMPLETED]: handleGameLoaded,
      [IgtMessages.GAME_LOAD_PROGRESS]: handleGameLoadProgress,
      [IgtMessages.GAME_LOAD_STARTED]: handleGameLoadStarted,
      [IgtMessages.GAME_ANIMATION_START]: handleSpinStarted,
      [IgtMessages.GAME_ANIMATION_COMPLETED]: handleSpinEnd,
      [IgtMessages.SETTINGS_UPDATE]: handleAudio,
      [IgtMessages.ERROR_MESSAGE]: handleError,
      [IgtMessages.RELOAD_GAME]: handleGameReset,
      [IgtMessages.GAME_IN_PROGRESS_RELOAD]: handleGameInProgressReload,
    }),
    [
      handleGameLoaded,
      handleGameLoadProgress,
      handleGameLoadStarted,
      handleSpinStarted,
      handleSpinEnd,
      handleAudio,
      handleError,
      handleGameReset,
      handleGameInProgressReload,
    ],
  );

  const providerEventHandler = useCallback(
    (event) => {
      let eventHandled = false;
      if (
        event?.data &&
        typeof event.data === 'string' &&
        event.data === 'reloadGame'
      ) {
        providerEventHandlerMapper[event.data]?.();
        eventHandled = true;
      }

      if (event?.data?.type) {
        providerEventHandlerMapper[event.data.type]?.(event.data.payload);
        eventHandled = true;
      }

      if (!eventHandled) {
        handleUnknownEvent(event.data);
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const gameProviderFrame =
        document.querySelector<HTMLIFrameElement>('#game-client')
          ?.contentWindow;
      const executeEvent = (event) => {
        if (gameProviderFrame) {
          gameProviderFrame.postMessage(event, '*');
        } else {
          Logger.rumError({
            name: 'Failed to get handler for provider',
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.IGT,
            },
          });
        }
      };

      const notifyAction = ({
        actionName,
        actionParam,
      }: {
        actionName: string;
        actionParam?: string;
      }) =>
        executeEvent({
          source: 'gameWindow',
          type: 'notifyAction',
          payload: {
            actionName,
            actionParam,
          },
        });

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
        executeEvent({
          source: 'gameWindow',
          type: 'togglePaytable',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.FORCE_END_SESSION) {
        notifyAction({ actionName: 'forceEndSession' });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP) {
        notifyAction({ actionName: 'gameHelp' });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR) {
        notifyAction({ actionName: 'errorMessageDismissed' });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          source: 'gameWindow',
          type: 'balanceChanged',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        executeEvent({
          source: 'gameWindow',
          type: 'settingsUpdate',
          payload: {
            settingName: 'sound',
            settingValue: e.data.payload ? 'on' : '',
          },
        });
        if (!audioSettingsReceived) {
          WrapperProxy.setAudio(e.data.payload);
        }
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.FREE_SPINS_DIALOG) {
        notifyAction({
          actionName: 'freeSpinDialog',
          actionParam: e.data.payload,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        notifyAction({ actionName: 'pauseGame' });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_RESUME) {
        notifyAction({ actionName: 'unpauseGame' });
      }
    },
    [audioSettingsReceived],
  );

  return { wrapperEventHandler, providerEventHandler };
};
